import { useState } from 'react';
import Toggle from './Toggle';

/**
 * The last step of building a quiz: name it, optionally time it, decide what students and other
 * teachers may see. Same four things teacher-web's old save modal asked for, so nothing changes for
 * the teacher - it just lives here now, because this screen owns saving.
 *
 * The time is the teacher's choice, and there is deliberately no default: a quiz left untimed runs
 * until the student finishes it. So the box starts empty, and a blank box means no limit.
 *
 * Editing an existing quiz asks the same four, filled in with what it already says.
 */
export function SaveQuizDialog(props: {
  questionCount: number;
  totalPoints: number;
  /** The quiz being edited, if this is an edit: its details fill the form. */
  quiz?: { name?: string; time_given?: number; isShowScore?: boolean; isPublic?: boolean };
  onCancel: () => void;
  onSave: (details: {
    name: string;
    /** Minutes, or 0 for a quiz with no time limit. */
    time_given: number;
    isShowScore: boolean;
    isPublic: boolean;
  }) => Promise<void>;
}) {
  // Editing starts from what the quiz already says; building starts from sensible defaults.
  const [name, setName] = useState(props.quiz?.name || '');
  // Blank means no time limit. A saved quiz with no limit comes back as 0, which is also blank.
  const [minutes, setMinutes] = useState(
    props.quiz?.time_given ? String(props.quiz.time_given) : '',
  );
  const [isShowScore, setIsShowScore] = useState(props.quiz ? props.quiz.isShowScore !== false : true);
  const [isPublic, setIsPublic] = useState(Boolean(props.quiz?.isPublic));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!name.trim()) {
      setError('Please give the quiz a name.');
      return;
    }
    // Empty is a real answer here: no limit. Anything typed has to be a sensible number of minutes.
    const typed = minutes.trim();
    const limit = typed === '' ? 0 : Number(typed);
    if (typed !== '' && (!Number.isFinite(limit) || limit < 1)) {
      setError('Enter the minutes students get, or leave it blank for no limit.');
      return;
    }
    if (limit > 180) {
      // The number box says 180, but a typed value ignores that - a quiz longer than three hours
      // is a mistake, not a choice.
      setError('A quiz can run for up to 180 minutes.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await props.onSave({
        name: name.trim(),
        time_given: limit,
        isShowScore,
        isPublic,
      });
    } catch (e: any) {
      // The builder stays open with every pick intact, so nothing is lost to a failed save.
      setError(e?.message || 'Could not save the quiz. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="aq-modal-bg">
      <div className="aq-modal">
        <h2>{props.quiz ? 'Update quiz' : 'Save quiz'}</h2>
        <p className="aq-notice">
          {props.questionCount} questions · {props.totalPoints} points
        </p>

        <label className="aq-field">
          <span>Quiz name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus />
        </label>

        <label className="aq-field">
          <span>Time (minutes) — leave blank for no limit</span>
          <input
            type="number"
            min={1}
            max={180}
            placeholder="No limit"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
          />
        </label>

        {/* The same two questions the old quiz's save popup asks, asked with the same switch. */}
        <div className="aq-toggle-row">
          <Toggle label="Show points to students" checked={isShowScore} onChange={setIsShowScore} />
        </div>

        <div className="aq-toggle-row">
          <Toggle label="Show this quiz to other teachers" checked={isPublic} onChange={setIsPublic} />
        </div>

        {error ? <p className="aq-error">{error}</p> : null}

        <div className="aq-modal-actions">
          <button type="button" className="aq-btn" onClick={props.onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aq-btn aq-btn-primary" onClick={save} disabled={saving}>
            {saving
              ? props.quiz
                ? 'Updating…'
                : 'Saving…'
              : props.quiz
                ? 'Update quiz'
                : 'Save quiz'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SaveQuizDialog;
